/**
 * @file url 解析
 * @author hejinfeng01
 */

/**
 * 将 http 协议 URL 转为 https。
 * 非 http 开头（如 https、blob:、data:、相对路径）原样返回。
 */
export function toHttps(url?: string): string {
    if (!url) {
        return '';
    }
    return url.replace(/^http:\/\//, 'https://');
}

/** 是否为 blob: 协议 URL */
export const isBlobUrl = (url: string): boolean => !!url && url.startsWith('blob:');

/** 是否为 data: 协议 URL */
export const isDataUrl = (url: string): boolean => !!url && url.startsWith('data:');

/** 判断 URL 是否为 BOS 资源，且没采用 V1 版本（可使用 x-bce-process 裁切） */
export function isBosUrl(src: string): boolean {
    try {
        const url = new URL(src);
        return url.hostname.includes('bcebos.com') && !src.includes('@');
    } catch {
        return false;
    }
}

/**
 * 从文件 URL 中解析 文件名、后缀、完整文件名
 */
export function getFileInfoFromUrl(url?: string | null) {
    if (!url?.length) {
        return {fullName: '', name: '', ext: ''};
    }

    try {
        // 先去掉 URL 参数、哈希
        const cleanUrl = url.split(/[?#]/)[0];

        // 获取最后一段（文件名）
        const segments = cleanUrl.split('/');
        const fullName = segments.pop() ?? '';

        if (!fullName) {
            return {fullName: '', name: '', ext: ''};
        }

        // 找最后一个 . 分割后缀（支持 .tar.gz 这种）
        const lastDotIndex = fullName.lastIndexOf('.');

        // 无后缀
        if (lastDotIndex <= 0) {
            return {
                fullName,
                name: fullName,
                ext: '',
            };
        }

        // 有后缀
        return {
            fullName,
            name: fullName.slice(0, lastDotIndex),
            ext: fullName.slice(lastDotIndex + 1).toLowerCase(), // 统一小写
        };
    } catch {
        return {fullName: '', name: '', ext: ''};
    }
}

/**
 * 新标签页打开站内页面。
 * 带 noopener 时 window.open 恒返回 null，需要判断是否被浏览器拦截的场景不能用。
 */
export function openInNewTab(url: string) {
    window.open(url, '_blank', 'noopener');
}

/** 从文件 URL 中解析扩展名（含点号），解析不到返回空串。 */
export function getExtFromUrl(url?: string | null): string {
    const {ext} = getFileInfoFromUrl(url);
    return ext ? `.${ext}` : '';
}
